import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  getIndustrialCostEconomicMovements,
  getIndustrialCostMovementClassifications,
  upsertIndustrialCostMovementClassification,
  type UpsertIndustrialCostMovementClassificationPayload,
} from '../services/industrialCostService';
import IndustrialCostSectionPlaceholder from '../components/IndustrialCostSectionPlaceholder';
import type {
  ConfidenceLevel,
  EconomicMovement,
  EconomicType,
  MovementClassification,
  MovementClassificationBadgeStatus,
  CostType,
} from '../types/industrialCost';

import { esNumberFormat } from '../utils/numberFormat';
function getErrorMessage(error: unknown): string {
  if (typeof error === 'object' && error !== null) {
    const maybeResponse = error as {
      response?: { data?: { detail?: string } };
      message?: string;
    };

    if (maybeResponse.response?.data?.detail) {
      return maybeResponse.response.data.detail;
    }

    if (maybeResponse.message) {
      return maybeResponse.message;
    }
  }

  return 'Se produjo un error inesperado.';
}

function formatDate(value: string | null | undefined): string {
  if (!value) {
    return '—';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat('es-ES').format(date);
}

function formatAmount(value: number): string {
  return esNumberFormat({
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function resolveClassificationStatus(
  movementId: string,
  classificationsMap: Map<string, MovementClassification>,
): MovementClassificationBadgeStatus {
  const classification = classificationsMap.get(movementId);

  if (!classification) {
    return 'unclassified';
  }

  if (classification.classification_status === 'ignored') {
    return 'ignored';
  }

  if (classification.classification_status === 'classified') {
    return 'classified';
  }

  return 'unclassified';
}

const CLASSIFICATION_STATUS_CONFIG: Record<
  MovementClassificationBadgeStatus,
  { label: string; color: string; bg: string; border: string }
> = {
  unclassified: {
    label: 'Sin clasificar',
    color: '#cbd5e1',
    bg: 'rgba(148,163,184,0.10)',
    border: 'rgba(148,163,184,0.22)',
  },
  classified: {
    label: 'Clasificado',
    color: '#22c55e',
    bg: 'rgba(34,197,94,0.10)',
    border: 'rgba(34,197,94,0.24)',
  },
  ignored: {
    label: 'Ignorado',
    color: '#f59e0b',
    bg: 'rgba(245,158,11,0.10)',
    border: 'rgba(245,158,11,0.24)',
  },
};

type ClassificationFormState = {
  classification_status: 'pending' | 'classified' | 'ignored';
  economic_type: EconomicType | '';
  cost_type: CostType | '';
  direct_cost_category: string;
  indirect_cost_category: string;
  notes: string;
  confidence: ConfidenceLevel | '';
  is_manual: boolean;
};

const initialForm: ClassificationFormState = {
  classification_status: 'classified',
  economic_type: '',
  cost_type: '',
  direct_cost_category: '',
  indirect_cost_category: '',
  notes: '',
  confidence: '',
  is_manual: true,
};

function buildFormFromClassification(
  classification?: MovementClassification | null,
): ClassificationFormState {
  if (!classification) {
    return initialForm;
  }

  return {
    classification_status: classification.classification_status,
    economic_type: classification.economic_type ?? '',
    cost_type: classification.cost_type ?? '',
    direct_cost_category: classification.direct_cost_category ?? '',
    indirect_cost_category: classification.indirect_cost_category ?? '',
    notes: classification.notes ?? '',
    confidence: classification.confidence ?? '',
    is_manual: classification.is_manual,
  };
}

export default function IndustrialCostClassificationView() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const [search, setSearch] = useState('');
  const [classificationFilter, setClassificationFilter] =
    useState<'all' | MovementClassificationBadgeStatus>('all');

  const [editingMovement, setEditingMovement] = useState<EconomicMovement | null>(null);
  const [form, setForm] = useState<ClassificationFormState>(initialForm);

  const [movementsQuery, classificationsQuery] = useQueries({
    queries: [
      {
        queryKey: ['industrial-cost-economic-movements'],
        queryFn: getIndustrialCostEconomicMovements,
      },
      {
        queryKey: ['industrial-cost-movement-classifications'],
        queryFn: getIndustrialCostMovementClassifications,
      },
    ],
  });

  const movements = movementsQuery.data ?? [];
  const classifications = classificationsQuery.data ?? [];

  const classificationsMap = useMemo(() => {
    return new Map<string, MovementClassification>(
      classifications.map((item) => [item.movement_id, item]),
    );
  }, [classifications]);

  const filteredMovements = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    return movements.filter((movement) => {
      const matchesSearch =
        normalizedSearch.length === 0 ||
        movement.description.toLowerCase().includes(normalizedSearch) ||
        (movement.supplier || '').toLowerCase().includes(normalizedSearch) ||
        (movement.document_ref || '').toLowerCase().includes(normalizedSearch);

      const classificationStatus = resolveClassificationStatus(
        movement.id,
        classificationsMap,
      );

      const matchesClassification =
        classificationFilter === 'all' ||
        classificationStatus === classificationFilter;

      return matchesSearch && matchesClassification;
    });
  }, [movements, search, classificationFilter, classificationsMap]);

  const upsertMutation = useMutation({
    mutationFn: (payload: UpsertIndustrialCostMovementClassificationPayload) =>
      upsertIndustrialCostMovementClassification(payload),
    onSuccess: async () => {
      enqueueSnackbar('Clasificación guardada correctamente.', {
        variant: 'success',
      });
      setEditingMovement(null);
      await queryClient.invalidateQueries({
        queryKey: ['industrial-cost-movement-classifications'],
      });
    },
    onError: (mutationError) => {
      enqueueSnackbar(getErrorMessage(mutationError), { variant: 'error' });
    },
  });

  const isLoading = movementsQuery.isLoading || classificationsQuery.isLoading;
  const isError = movementsQuery.isError || classificationsQuery.isError;
  const error = movementsQuery.error ?? classificationsQuery.error;

  const handleOpenEdit = (movement: EconomicMovement) => {
    setEditingMovement(movement);
    setForm(buildFormFromClassification(classificationsMap.get(movement.id)));
  };

  const handleChange = <K extends keyof ClassificationFormState>(
    key: K,
    value: ClassificationFormState[K],
  ) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = () => {
    if (!editingMovement) return;

    const payload: UpsertIndustrialCostMovementClassificationPayload = {
      movement_id: editingMovement.id,
      classification_status: form.classification_status,
      economic_type: form.classification_status === 'ignored'
        ? null
        : form.economic_type || null,
      cost_type:
        form.classification_status === 'ignored' || form.economic_type !== 'cost'
          ? null
          : form.cost_type || null,
      direct_cost_category:
        form.classification_status === 'ignored' ||
        form.economic_type !== 'cost' ||
        form.cost_type !== 'direct'
          ? null
          : form.direct_cost_category.trim() || null,
      indirect_cost_category:
        form.classification_status === 'ignored' ||
        form.economic_type !== 'cost' ||
        form.cost_type !== 'indirect'
          ? null
          : form.indirect_cost_category.trim() || null,
      notes: form.notes.trim() || null,
      confidence:
        form.classification_status === 'ignored' ? null : form.confidence || null,
      is_manual: true,
    };

    upsertMutation.mutate(payload);
  };

  const canSave =
    form.classification_status === 'ignored' ||
    (!!form.economic_type &&
      (form.economic_type === 'expense' ||
        (!!form.cost_type &&
          (form.cost_type !== 'direct' || form.direct_cost_category.trim().length > 0) &&
          (form.cost_type !== 'indirect' ||
            form.indirect_cost_category.trim().length > 0))));

  return (
    <>
      <Stack spacing={3}>
        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            border: '1px solid rgba(148,163,184,0.18)',
            bgcolor: 'rgba(15,23,42,0.66)',
            backdropFilter: 'blur(10px)',
            px: { xs: 2.5, md: 3 },
            py: { xs: 2.5, md: 3 },
          }}
        >
          <Stack spacing={1.25}>
            <Typography variant="h5" sx={{ color: '#f8fafc', fontWeight: 900 }}>
              Clasificación
            </Typography>

            <Typography variant="body1" sx={{ color: '#cbd5e1', lineHeight: 1.7 }}>
              Esta vista es la mesa de decisión económica sobre los movimientos:
              separar gasto y coste, y dentro del coste distinguir directo e indirecto.
            </Typography>

            <Typography variant="body2" sx={{ color: '#94a3b8', lineHeight: 1.65 }}>
              La lectura del dato bruto ya ocurre en Movimientos. Aquí el trabajo es
              clasificar con criterio experto y dejar base válida para etapas posteriores.
            </Typography>
          </Stack>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            border: '1px solid rgba(148,163,184,0.18)',
            bgcolor: 'rgba(15,23,42,0.66)',
            backdropFilter: 'blur(10px)',
            px: 3,
            py: 3,
          }}
        >
          <Stack spacing={2.25}>
            <Typography variant="h6" sx={{ color: '#f8fafc', fontWeight: 800 }}>
              Filtros de trabajo
            </Typography>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
                gap: 1.5,
              }}
            >
              <TextField
                label="Buscar"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                fullWidth
                size="small"
                placeholder="Descripción, proveedor o referencia"
              />

              <TextField
                select
                label="Estado"
                value={classificationFilter}
                onChange={(e) =>
                  setClassificationFilter(
                    e.target.value as 'all' | MovementClassificationBadgeStatus,
                  )
                }
                fullWidth
                size="small"
              >
                <MenuItem value="all">Todas</MenuItem>
                <MenuItem value="unclassified">Sin clasificar</MenuItem>
                <MenuItem value="classified">Clasificadas</MenuItem>
                <MenuItem value="ignored">Ignoradas</MenuItem>
              </TextField>
            </Box>
          </Stack>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            border: '1px solid rgba(148,163,184,0.18)',
            bgcolor: 'rgba(15,23,42,0.66)',
            backdropFilter: 'blur(10px)',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ px: 3, py: 2.5, borderBottom: '1px solid rgba(148,163,184,0.14)' }}>
            <Typography variant="h6" sx={{ color: '#f8fafc', fontWeight: 800 }}>
              Mesa de clasificación
            </Typography>
            <Typography variant="body2" sx={{ color: '#94a3b8', mt: 0.5 }}>
              Selecciona un movimiento y registra su clasificación económica.
            </Typography>
          </Box>

          {isError ? (
            <Box sx={{ p: 3 }}>
              <Alert severity="error">{getErrorMessage(error)}</Alert>
            </Box>
          ) : isLoading ? (
            <Box sx={{ p: 3 }}>
              <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                Cargando movimientos para clasificación...
              </Typography>
            </Box>
          ) : movements.length === 0 ? (
            <Box sx={{ p: 3 }}>
              <IndustrialCostSectionPlaceholder
                title="Sin movimientos para clasificar"
                description="Primero necesitas movimientos económicos cargados para poder abrir la fase de clasificación."
              />
            </Box>
          ) : filteredMovements.length === 0 ? (
            <Box sx={{ p: 3 }}>
              <IndustrialCostSectionPlaceholder
                title="Sin resultados para los filtros actuales"
                description="Ajusta los filtros para volver a mostrar movimientos pendientes o ya clasificados."
              />
            </Box>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }}>Fecha</TableCell>
                    <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }}>Proveedor</TableCell>
                    <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }}>Descripción</TableCell>
                    <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }} align="right">
                      Importe
                    </TableCell>
                    <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }}>Estado</TableCell>
                    <TableCell
                      align="right"
                      sx={{ color: '#94a3b8', fontWeight: 800, minWidth: 160 }}
                    >
                      Acción
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {filteredMovements.map((movement) => {
                    const classificationStatus = resolveClassificationStatus(
                      movement.id,
                      classificationsMap,
                    );
                    const cfg = CLASSIFICATION_STATUS_CONFIG[classificationStatus];

                    return (
                      <TableRow key={movement.id} hover>
                        <TableCell sx={{ color: '#cbd5e1' }}>
                          {formatDate(movement.movement_date)}
                        </TableCell>
                        <TableCell sx={{ color: '#cbd5e1' }}>
                          {movement.supplier || '—'}
                        </TableCell>
                        <TableCell sx={{ color: '#f8fafc', fontWeight: 700 }}>
                          {movement.description}
                        </TableCell>
                        <TableCell align="right" sx={{ color: '#f8fafc', fontWeight: 700 }}>
                          {formatAmount(movement.amount)}
                        </TableCell>
                        <TableCell sx={{ color: '#cbd5e1' }}>
                          <Chip
                            label={cfg.label}
                            size="small"
                            sx={{
                              bgcolor: cfg.bg,
                              color: cfg.color,
                              border: `1px solid ${cfg.border}`,
                              fontWeight: 800,
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleOpenEdit(movement)}
                            sx={{
                              textTransform: 'none',
                              fontWeight: 700,
                              borderColor: 'rgba(56,189,248,0.28)',
                              color: '#38bdf8',
                            }}
                          >
                            Clasificar
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          )}
        </Paper>
      </Stack>

      <Dialog
        open={!!editingMovement}
        onClose={() => !upsertMutation.isPending && setEditingMovement(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Clasificar movimiento</DialogTitle>

        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {editingMovement ? (
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: '1px solid rgba(148,163,184,0.18)',
                  bgcolor: 'rgba(15,23,42,0.04)',
                  px: 2,
                  py: 1.5,
                }}
              >
                <Stack spacing={0.75}>
                  <Typography variant="body2" sx={{ fontWeight: 800 }}>
                    {editingMovement.description}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {editingMovement.supplier || 'Sin proveedor'} · {formatAmount(editingMovement.amount)}
                  </Typography>
                </Stack>
              </Paper>
            ) : null}

            <TextField
              select
              label="Estado de clasificación"
              value={form.classification_status}
              onChange={(e) =>
                handleChange(
                  'classification_status',
                  e.target.value as ClassificationFormState['classification_status'],
                )
              }
              fullWidth
              size="small"
            >
              <MenuItem value="classified">Clasificado</MenuItem>
              <MenuItem value="ignored">Ignorado</MenuItem>
              <MenuItem value="pending">Pendiente</MenuItem>
            </TextField>

            <TextField
              select
              label="Tipo económico"
              value={form.economic_type}
              onChange={(e) =>
                handleChange('economic_type', e.target.value as EconomicType | '')
              }
              fullWidth
              size="small"
              disabled={form.classification_status === 'ignored'}
            >
              <MenuItem value="">Seleccionar</MenuItem>
              <MenuItem value="expense">Gasto</MenuItem>
              <MenuItem value="cost">Coste</MenuItem>
            </TextField>

            <TextField
              select
              label="Tipo de coste"
              value={form.cost_type}
              onChange={(e) =>
                handleChange('cost_type', e.target.value as CostType | '')
              }
              fullWidth
              size="small"
              disabled={
                form.classification_status === 'ignored' ||
                form.economic_type !== 'cost'
              }
            >
              <MenuItem value="">Seleccionar</MenuItem>
              <MenuItem value="direct">Directo</MenuItem>
              <MenuItem value="indirect">Indirecto</MenuItem>
            </TextField>

            {form.economic_type === 'cost' && form.cost_type === 'direct' ? (
              <TextField
                label="Categoría de coste directo"
                value={form.direct_cost_category}
                onChange={(e) =>
                  handleChange('direct_cost_category', e.target.value)
                }
                fullWidth
                size="small"
              />
            ) : null}

            {form.economic_type === 'cost' && form.cost_type === 'indirect' ? (
              <TextField
                label="Categoría de coste indirecto"
                value={form.indirect_cost_category}
                onChange={(e) =>
                  handleChange('indirect_cost_category', e.target.value)
                }
                fullWidth
                size="small"
              />
            ) : null}

            <TextField
              select
              label="Confianza"
              value={form.confidence}
              onChange={(e) =>
                handleChange('confidence', e.target.value as ConfidenceLevel | '')
              }
              fullWidth
              size="small"
              disabled={form.classification_status === 'ignored'}
            >
              <MenuItem value="">Sin informar</MenuItem>
              <MenuItem value="high">Alta</MenuItem>
              <MenuItem value="medium">Media</MenuItem>
              <MenuItem value="low">Baja</MenuItem>
            </TextField>

            <TextField
              label="Notas"
              value={form.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              fullWidth
              size="small"
              multiline
              minRows={3}
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setEditingMovement(null)}
            disabled={upsertMutation.isPending}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!canSave || upsertMutation.isPending}
            sx={{
              textTransform: 'none',
              fontWeight: 800,
              bgcolor: '#38bdf8',
              color: '#0f172a',
              '&:hover': { bgcolor: '#38bdf8', filter: 'brightness(1.06)' },
            }}
          >
            {upsertMutation.isPending ? 'Guardando...' : 'Guardar clasificación'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}